import React from 'react';

const ColorPickerModal = ({ 
  isOpen, 
  onClose, 
  props, 
  onColorSelect 
}) => {
  if (!isOpen) return null;

  // Color Picker Data and Logic
  const colorPalette = [
    { name: 'Red', value: 'red', hex: '#ef4444', searchTerms: ['red', 'crimson', 'scarlet', 'ruby', 'burgundy'] },
    { name: 'Orange', value: 'orange', hex: '#f97316', searchTerms: ['orange', 'amber', 'tangerine', 'peach'] },
    { name: 'Yellow', value: 'yellow', hex: '#eab308', searchTerms: ['yellow', 'gold', 'golden', 'honey', 'cream'] },
    { name: 'Green', value: 'green', hex: '#22c55e', searchTerms: ['green', 'emerald', 'forest', 'olive', 'lime'] },
    { name: 'Blue', value: 'blue', hex: '#3b82f6', searchTerms: ['blue', 'navy', 'azure', 'cobalt', 'teal'] },
    { name: 'Purple', value: 'purple', hex: '#a855f7', searchTerms: ['purple', 'violet', 'lavender', 'plum', 'magenta'] },
    { name: 'Pink', value: 'pink', hex: '#ec4899', searchTerms: ['pink', 'rose', 'blush', 'fuchsia'] },
    { name: 'Brown', value: 'brown', hex: '#a3a3a3', searchTerms: ['brown', 'tan', 'beige', 'khaki', 'wood', 'oak'] },
    { name: 'Black', value: 'black', hex: '#000000', searchTerms: ['black', 'charcoal', 'ebony', 'dark'] },
    { name: 'White', value: 'white', hex: '#ffffff', searchTerms: ['white', 'ivory', 'cream', 'pearl', 'snow'] },
    { name: 'Gray', value: 'gray', hex: '#6b7280', searchTerms: ['gray', 'grey', 'silver', 'slate', 'pewter'] },
    { name: 'Gold', value: 'gold', hex: '#fbbf24', searchTerms: ['gold', 'golden', 'brass', 'bronze'] }
  ];

  const getColorStats = () => {
    const colorCounts = {};
    
    colorPalette.forEach(color => {
      colorCounts[color.value] = 0;
    });
    
    props.forEach(prop => {
      // Check prop name, tags, and materials for color mentions
      const searchText = [
        prop.name,
        ...(prop.tags || []),
        ...(prop.material || []),
        prop.category
      ].join(' ').toLowerCase();
      
      colorPalette.forEach(color => {
        if (color.searchTerms.some(term => searchText.includes(term))) {
          colorCounts[color.value]++;
        }
      });
    });
    
    return colorCounts;
  };

  const handleColorSelect = (color) => {
    onColorSelect(color);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 color-picker-modal">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-pink-600 to-rose-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-2">🎨 Color-Based Search</h2>
              <p className="text-pink-100">Find props by color. Click any color to search for matching items.</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-pink-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Color Picker Content */}
        <div className="p-8 overflow-y-auto max-h-[75vh]">
          {/* Color Palette Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-6">
            {colorPalette.map(color => {
              const colorStats = getColorStats();
              const count = colorStats[color.value] || 0;
              
              return (
                <button
                  key={color.value}
                  onClick={() => handleColorSelect(color)}
                  className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl"
                  style={{
                    background: color.hex === '#ffffff' 
                      ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
                      : `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}dd 100%)`,
                    border: color.hex === '#ffffff' ? '2px solid #e5e7eb' : 'none'
                  }}
                >
                  {/* Color Circle */}
                  <div className="aspect-square p-4 flex flex-col items-center justify-center">
                    {/* Main Color Circle */}
                    <div 
                      className="w-16 h-16 rounded-full shadow-lg group-hover:shadow-xl transition-shadow duration-300 mb-2"
                      style={{
                        backgroundColor: color.hex,
                        border: color.hex === '#ffffff' ? '2px solid #d1d5db' : 'none'
                      }}
                    ></div>
                    
                    {/* Color Name */}
                    <div className={`text-sm font-bold ${color.hex === '#000000' || color.hex === '#6b7280' ? 'text-white' : color.hex === '#ffffff' ? 'text-gray-800' : 'text-white'}`}>
                      {color.name}
                    </div>
                    
                    {/* Prop Count */}
                    <div className={`text-xs opacity-90 ${color.hex === '#000000' || color.hex === '#6b7280' ? 'text-white' : color.hex === '#ffffff' ? 'text-gray-600' : 'text-white'}`}>
                      {count} props
                    </div>
                    
                    {/* Hover Effect Overlay */}
                    <div className="absolute inset-0 bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-xl"></div>
                  </div>
                  
                  {/* Color Variants (bottom strip) */}
                  <div className="h-2 flex">
                    {color.searchTerms.slice(1, 4).map((term, index) => {
                      const shade = index === 0 ? '80%' : index === 1 ? '60%' : '40%';
                      return (
                        <div 
                          key={term}
                          className="flex-1"
                          style={{
                            backgroundColor: `${color.hex}${Math.floor(255 * (parseInt(shade) / 100)).toString(16).padStart(2, '0')}`
                          }}
                        ></div>
                      );
                    })}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Color Statistics */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-4">📊 Color Distribution in Your Props:</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(getColorStats())
                .filter(([color, count]) => count > 0)
                .sort(([,a], [,b]) => b - a)
                .map(([colorValue, count]) => {
                  const color = colorPalette.find(c => c.value === colorValue);
                  return (
                    <div key={colorValue} className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full border border-gray-200"
                        style={{ backgroundColor: color?.hex }}
                      ></div>
                      <span className="text-sm text-gray-700">
                        {color?.name}: <span className="font-medium">{count}</span>
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
          
          {/* Usage Tips */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">💡 How color search works:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Searches prop names, tags, and materials for color keywords</li>
              <li>• Each color includes related terms (e.g., "red" finds "scarlet", "ruby", "burgundy")</li>
              <li>• Numbers show how many props contain that color</li>
              <li>• Click any color to search for matching props</li>
              <li>• Try combining with other filters for precise results</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPickerModal;